import React from 'react';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import { ContentCopy, IosShare } from '@mui/icons-material';

type ShareRow = {
  label: string;
  value: string | number | null | undefined;
};

interface DailyAstrologyShareCardProps {
  title: string;
  subtitle: string;
  date: string;
  location: string;
  rows: ShareRow[];
  textMessage?: string;
  imageFileName?: string;
  shareTitle?: string;
  imageButtonLabel?: string;
  textButtonLabel?: string;
  copyButtonLabel?: string;
}

const APP_URL = 'https://kundlistarstudio.com?utm_source=share&utm_medium=website&utm_campaign=daily_astrology_card';
let cachedLogoDataUri: string | null = null;

const escapeXml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const trimText = (value: string, max = 34) => (
  value.length > max ? `${value.slice(0, max - 1)}…` : value
);

const blobToDataUri = (blob: Blob) => (
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  })
);

const getLogoDataUri = async () => {
  if (cachedLogoDataUri) return cachedLogoDataUri;
  const response = await fetch('/logo.png');
  if (!response.ok) return '';
  cachedLogoDataUri = await blobToDataUri(await response.blob());
  return cachedLogoDataUri;
};

const compactShareLabel = (label: string, fallback: string) => {
  const compact = label
    .replace(/share\s+as/gi, '')
    .replace(/share/gi, '')
    .replace(/शेयर करें/g, '')
    .trim();
  return compact || fallback;
};

const buildSvg = ({ title, subtitle, date, location, rows, logoDataUri }: {
  title: string;
  subtitle: string;
  date: string;
  location: string;
  rows: ShareRow[];
  logoDataUri?: string;
}) => {
  const safeRows = rows
    .filter((row) => row.value !== null && row.value !== undefined && String(row.value).trim())
    .slice(0, 10);
  const rowStart = 420;
  const rowHeight = 72;
  const rowsHeight = Math.max(140, safeRows.length * rowHeight + 34);

  return `
<svg width="1080" height="1350" viewBox="0 0 1080 1350" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="1080" height="1350" fill="#0B2418"/>
  <circle cx="920" cy="120" r="220" fill="#F5C542" opacity="0.13"/>
  <circle cx="140" cy="1150" r="260" fill="#2E7D4F" opacity="0.22"/>
  <rect x="70" y="70" width="940" height="1210" rx="44" fill="#123522" stroke="#F5C542" stroke-opacity="0.36" stroke-width="3"/>
  ${logoDataUri ? `<image href="${logoDataUri}" x="466" y="106" width="72" height="72" preserveAspectRatio="xMidYMid meet"/>` : ''}
  <text x="${logoDataUri ? 592 : 540}" y="155" text-anchor="${logoDataUri ? 'start' : 'middle'}" fill="#F5C542" font-family="Arial, sans-serif" font-size="48" font-weight="800">Kundlistar</text>
  <text x="540" y="275" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="70" font-weight="900">${escapeXml(trimText(title, 26))}</text>
  <text x="540" y="335" text-anchor="middle" fill="#D1FAE5" font-family="Arial, sans-serif" font-size="34" font-weight="700">${escapeXml(trimText(subtitle, 45))}</text>
  <text x="540" y="385" text-anchor="middle" fill="#F5C542" font-family="Arial, sans-serif" font-size="30" font-weight="800">${escapeXml(trimText(`${date} • ${location}`, 54))}</text>
  <rect x="120" y="${rowStart}" width="840" height="${rowsHeight}" rx="28" fill="#FFFFFF" opacity="0.08" stroke="#F5C542" stroke-opacity="0.28" stroke-width="2"/>
  ${safeRows.map((row, index) => {
    const y = rowStart + 58 + index * rowHeight;
    return `
      <text x="160" y="${y}" fill="#BFE6CF" font-family="Arial, sans-serif" font-size="31" font-weight="700">${escapeXml(trimText(row.label, 24))}</text>
      <text x="920" y="${y}" text-anchor="end" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="31" font-weight="800">${escapeXml(trimText(String(row.value), 32))}</text>
      ${index < safeRows.length - 1 ? `<line x1="150" y1="${y + 26}" x2="930" y2="${y + 26}" stroke="#FFFFFF" stroke-opacity="0.16"/>` : ''}
    `;
  }).join('')}
  <text x="540" y="1138" text-anchor="middle" fill="#F5C542" font-family="Arial, sans-serif" font-size="42" font-weight="900">All-in-one Vedic Astrology App</text>
  <text x="540" y="1195" text-anchor="middle" fill="#D1FAE5" font-family="Arial, sans-serif" font-size="28" font-weight="700">Kundli • Panchang • Muhurat • Gochar • Tarot</text>
  <text x="540" y="1248" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="30" font-weight="900">Download: kundlistarstudio.com</text>
</svg>`.trim();
};

const svgToPngBlob = async (svg: string) => {
  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to create image.');
    context.drawImage(image, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Unable to create image.'));
      }, 'image/png', 1);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export default function DailyAstrologyShareCard({
  title,
  subtitle,
  date,
  location,
  rows,
  textMessage,
  imageFileName = 'kundlistar-daily-astrology-card.png',
  shareTitle = 'Kundlistar Daily Astrology Card',
  imageButtonLabel = 'Share Image',
  textButtonLabel = 'Share Text',
  copyButtonLabel = 'Copy Text'
}: DailyAstrologyShareCardProps) {
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const normalizedRows = rows.filter((row) => row.value !== null && row.value !== undefined && String(row.value).trim());
  const fallbackText = textMessage || [
    `${title} - ${date}`,
    location,
    '',
    ...normalizedRows.map((row) => `${row.label}: ${row.value}`),
    '',
    'Shared via Kundlistar',
    `Download App: ${APP_URL}`
  ].join('\n');

  const shareImage = async () => {
    try {
      setBusy(true);
      const logoDataUri = await getLogoDataUri();
      const blob = await svgToPngBlob(buildSvg({ title, subtitle, date, location, rows, logoDataUri }));
      const file = new File([blob], imageFileName, { type: 'image/png' });
      const canShareFile = typeof navigator.share === 'function' && Boolean(navigator.canShare?.({ files: [file] }));

      if (canShareFile) {
        await navigator.share({
          title: shareTitle,
          text: fallbackText,
          files: [file]
        });
        setNotice('Share dialog opened.');
      } else {
        downloadBlob(blob, imageFileName);
        setNotice('Image downloaded. You can share it on WhatsApp or social media.');
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') setNotice(error?.message || 'Unable to share image.');
    } finally {
      setBusy(false);
    }
  };

  const shareText = async () => {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: shareTitle, text: fallbackText });
        setNotice('Share dialog opened.');
      } else {
        await navigator.clipboard.writeText(fallbackText);
        setNotice('Text copied to clipboard.');
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') setNotice(error?.message || 'Unable to share text.');
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(fallbackText);
      setNotice('Text copied to clipboard.');
    } catch (_) {
      setNotice('Copy failed. Please try Share Text.');
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid rgba(46, 125, 79, 0.22)',
        borderRadius: 3,
        p: 2,
        bgcolor: 'rgba(46, 125, 79, 0.04)'
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {date} · {location}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<IosShare />}
            onClick={shareImage}
            disabled={busy}
            sx={{
              bgcolor: '#2E7D4F',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1F5F3A' }
            }}
          >
            {busy ? 'Preparing...' : compactShareLabel(imageButtonLabel, 'Image')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={copyText}
            sx={{
              borderColor: '#2E7D4F',
              color: '#2E7D4F',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#1F5F3A',
                bgcolor: 'rgba(46, 125, 79, 0.06)'
              }
            }}
          >
            {copyButtonLabel}
          </Button>
          <Button
            variant="text"
            startIcon={<IosShare />}
            onClick={shareText}
            sx={{
              color: '#2E7D4F',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(46, 125, 79, 0.06)' }
            }}
          >
            {compactShareLabel(textButtonLabel, 'Text')}
          </Button>
        </Stack>
      </Stack>
      <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice('')}>
        <Alert severity="info" onClose={() => setNotice('')} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
